/**
 * The hand that was just won, held long enough to read.
 *
 * Poker settles and re-deals inside one reduction: the last call closes the
 * betting, the pot is awarded, the hole cards move into their public zones,
 * `endIf` fires, `onEnd` deals again, and dealing empties those zones. Every
 * one of those steps happens before a packet leaves the server, so the
 * showdown - the entire point of the hand - was never on screen for a single
 * frame. Players saw their stack change and had to work out why.
 *
 * This is the five seconds in which it can be read. Same bargain `TrickWon`
 * makes, and for the same reason: the game records what happened
 * (`lastHand`), the real table moves on, and the client holds the record up.
 *
 * ## Why five seconds and not two
 *
 * A trick is one card each and the question is "who won". A showdown is two
 * cards each, a five-card board, a named hand per player and an amount - and
 * the question people actually ask is "how did THAT beat mine". Timed against
 * reading it out: four players at a showdown is about four seconds of reading.
 *
 * ## Shown on the table
 *
 * Because everybody needs it, including - especially - the players who lost.
 * A showdown on the winner's own phone tells the one person who already knows.
 */

import { useRef, useState } from 'react';
import { useCounterEvent } from '../render/use-counter-event.js';
import { SHOWDOWN_MS, readShowdown, showdownLine, titled } from '../render/showdown.js';
import type { Showdown as Result } from '../render/showdown.js';
import type { MatchView } from '../net/match.js';
import { Card } from '../render/Card.js';

export function Showdown({ view }: { view: MatchView }) {
  const [showing, setShowing] = useState<Result | null>(null);
  const timer = useRef<number | null>(null);

  const result = readShowdown(view);

  /*
   * Read through a ref so the event depends on the counter alone. Naming a
   * freshly-built object in a dependency array is what kept `TrickWon` on
   * screen until the next trick replaced it.
   */
  const latest = useRef(result);
  latest.current = result;

  useCounterEvent(result?.n ?? 0, () => {
    if (!latest.current) return;
    setShowing(latest.current);

    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShowing(null), SHOWDOWN_MS);
  });

  if (!showing) return null;

  const nameOf = (seat: number): string =>
    view.seats.find((chair) => chair.seat === seat)?.displayName ?? `Seat ${String(seat + 1)}`;

  return (
    <div className="showdown" role="status">
      {/* A panel, so the labels have something to belong to. Laid straight on
          the felt they collided with each other and with the deck under
          them - a hand's name is much wider than the two cards it names. */}
      <div className="showdown-panel">
        {showing.board.length > 0 ? (
          <div className="showdown-board">
            {showing.board.map((card) => (
              <Card key={card} card={card} small />
            ))}
          </div>
        ) : null}

        <div className="showdown-hands">
          {showing.hands.map((entry) => {
            const won = entry.won > 0;
            return (
              <div key={entry.seat} className={won ? 'showdown-hand won' : 'showdown-hand'}>
                {/* Above the cards, so the eye lands on the verdict and then
                    on what produced it - rather than comparing five cards
                    itself. */}
                {won ? <span className="showdown-flag">Winner</span> : null}
                <div className="showdown-cards">
                  {entry.cards.map((card) => (
                    <Card key={card} card={card} small />
                  ))}
                </div>
                <span className="showdown-rank">{titled(entry.hand)}</span>
                <span className="showdown-who">{nameOf(entry.seat)}</span>
              </div>
            );
          })}
        </div>

        {/*
          Said in words as well as in colour and position.

          A screen reader gets nothing from a gold border, and neither does
          anybody glancing up from across the room.
        */}
        <p className="showdown-line">{showdownLine(showing, nameOf)}</p>
      </div>
    </div>
  );
}
